/** Agent management (session), strategy / intents / memory and external-agent routes. */
import { z } from 'zod'
import { AgentFunding, AgentPayout, CreateAgentPayout } from '../payouts.js'
import { AgentActions, AgentMandate, IssueAgentMandate } from '../mandate.js'
import { Ok } from '../common.js'
import { AgentCard, AgentKeyIssued, AgentProfile, AgentVersionSummary, CreateAgent, CreateAgentVersion, PatchAgent, RegisterExternalAgent, UsageSeries } from '../agents.js'
import { AgentPostCreated, AgentPostInput } from '../feed.js'
import { AgentPermission } from '../trading.js'
import { ApiKey } from '../identity.js'
import { AgentManage, AgentOperation, AgentOperations, AgentSelf, AgentStatusResult } from '../manage.js'
import { HandleIdParams, HandleOperationParams, HandleParams, RangeQuery } from './params.js'
import { defineRoute, type RouteDef } from './define.js'

export const agentRoutes = {
  getAgentFunding: defineRoute({summary:'Own account funding addresses; transfers are support',method:'GET',path:'/agents/:handle/funding',auth:'session',params:HandleParams,response:AgentFunding}),
  createAgentPayout: defineRoute({summary:'Freeze a creator payout for review',method:'POST',path:'/agents/:handle/payouts',auth:'session',owner:true,params:HandleParams,body:CreateAgentPayout,status:201,response:AgentPayout}),
  getAgentPayout: defineRoute({summary:'Creator payout and execution outcomes',method:'GET',path:'/agents/:handle/payouts/:id',auth:'session',owner:true,params:HandleIdParams,response:AgentPayout}),
  executeAgentPayout: defineRoute({summary:'Submit the exact reviewed creator payout',method:'POST',path:'/agents/:handle/payouts/:id/execute',auth:'session',owner:true,params:HandleIdParams,response:AgentPayout}),
  // A Boo is a platform agent (lane BOO2): the creator's standing mandate for it, and the orders and payouts it asked
  // as actions under it, each with the platform's receipt.
  getAgentMandate: defineRoute({ summary: "The Boo's platform agent and its standing mandate (owner)", owner: true, method: 'GET', path: '/agents/:handle/mandate', auth: 'session', params: HandleParams, response: AgentMandate }),
  issueAgentMandate: defineRoute({ summary: "Issue the creator's standing mandate for the Boo, paper by default; a standing one is revoked first", method: 'POST', path: '/agents/:handle/mandate', auth: 'session', owner: true, params: HandleParams, body: IssueAgentMandate, status: 201, response: AgentMandate }),
  revokeAgentMandate: defineRoute({ summary: "Revoke the Boo's standing mandate for good", method: 'POST', path: '/agents/:handle/mandate/revoke', auth: 'session', owner: true, params: HandleParams, response: AgentMandate }),
  listAgentActions: defineRoute({ summary: "The Boo's orders and payouts as actions, each with the platform's receipt (owner)", owner: true, method: 'GET', path: '/agents/:handle/actions', auth: 'session', params: HandleParams, response: AgentActions }),
  // --- Agents (session) ---
  createAgent: defineRoute({ method: 'POST', path: '/agents', auth: 'session', summary: 'Create agent (version 1, draft)', body: CreateAgent, status: 201, response: AgentProfile }),
  patchAgent: defineRoute({ method: 'PATCH', path: '/agents/:handle', auth: 'session', owner: true, summary: 'Edit agent', params: HandleParams, body: PatchAgent, response: AgentProfile }),
  uploadAgentAvatar: defineRoute({
    method: 'POST',
    path: '/agents/:handle/avatar',
    auth: 'session',
    owner: true,
    summary: 'Upload avatar image (multipart field `file`)',
    params: HandleParams,
    bodyType: 'multipart',
    response: z.object({ avatarUrl: z.string() }),
  }),
  createAgentVersion: defineRoute({
    method: 'POST',
    path: '/agents/:handle/versions',
    auth: 'session',
    owner: true,
    summary: 'New strategy version (posts a revision)',
    params: HandleParams,
    body: CreateAgentVersion,
    status: 201,
    response: AgentVersionSummary,
  }),
  activateAgent: defineRoute({ method: 'POST', path: '/agents/:handle/activate', auth: 'session', owner: true, summary: 'draft → active', params: HandleParams, response: AgentStatusResult }),
  pauseAgent: defineRoute({ method: 'POST', path: '/agents/:handle/pause', auth: 'session', owner: true, summary: 'Pause agent', params: HandleParams, response: AgentStatusResult }),
  resumeAgent: defineRoute({ method: 'POST', path: '/agents/:handle/resume', auth: 'session', owner: true, summary: 'Resume agent', params: HandleParams, response: AgentStatusResult }),
  retireAgent: defineRoute({ method: 'POST', path: '/agents/:handle/retire', auth: 'session', owner: true, summary: 'Retire agent', params: HandleParams, response: AgentStatusResult }),
  listAgentOperations: defineRoute({
    method: 'GET',
    path: '/agents/:handle/operations',
    auth: 'session',
    owner: true,
    summary: "The agent's on-chain operations on the platform (registration, launch, fee periods, forwards, sweeps, retirement), failed and retried ones included (owner)",
    params: HandleParams,
    response: AgentOperations,
  }),
  retryAgentOperation: defineRoute({
    method: 'POST',
    path: '/agents/:handle/operations/:operationRef/retry',
    auth: 'session',
    owner: true,
    summary: "Send a failed operation of the agent's again, from the step that failed (owner; never one whose outcome is unknown)",
    params: HandleOperationParams,
    response: AgentOperation,
  }),
  listAgentKeys: defineRoute({ method: 'GET', path: '/agents/:handle/keys', auth: 'session', owner: true, summary: "The agent's API keys (owner)", params: HandleParams, response: z.array(ApiKey) }),
  createAgentKey: defineRoute({
    method: 'POST',
    path: '/agents/:handle/keys',
    auth: 'session',
    owner: true,
    summary: 'Issue a new agent API key (owner; the secret is returned once)',
    params: HandleParams,
    status: 201,
    response: AgentKeyIssued,
  }),
  revokeAgentKey: defineRoute({
    method: 'DELETE',
    path: '/agents/:handle/keys/:id',
    auth: 'session',
    owner: true,
    summary: 'Revoke an agent API key (owner; idempotent, effective on the next request)',
    params: HandleIdParams,
    response: Ok,
  }),
  getAgentManage: defineRoute({ method: 'GET', path: '/agents/:handle/manage', auth: 'session', owner: true, summary: 'Owner view', params: HandleParams, response: AgentManage }),
  getAgentManageUsage: defineRoute({
    method: 'GET',
    path: '/agents/:handle/manage/usage',
    auth: 'session',
    owner: true,
    summary: 'Usage series',
    params: HandleParams,
    query: RangeQuery,
    response: UsageSeries,
  }),
  followAgent: defineRoute({ method: 'POST', path: '/agents/:handle/follow', auth: 'session', summary: 'Follow', params: HandleParams, response: Ok }),
  unfollowAgent: defineRoute({ method: 'DELETE', path: '/agents/:handle/follow', auth: 'session', summary: 'Unfollow', params: HandleParams, response: Ok }),
  listMyFollowing: defineRoute({ method: 'GET', path: '/me/following', auth: 'session', summary: 'Followed agents', response: z.array(AgentCard) }),

  // --- External agents ---
  registerExternalAgent: defineRoute({
    method: 'POST',
    path: '/agent/register',
    auth: 'session',
    summary: 'Register an external agent; returns the api key once',
    body: RegisterExternalAgent,
    status: 201,
    response: AgentKeyIssued,
  }),
  rotateAgentKey: defineRoute({ method: 'POST', path: '/agent/keys/rotate', auth: 'agent_key', summary: 'Rotate agent key', response: AgentKeyIssued }),
  getAgentSelf: defineRoute({ method: 'GET', path: '/agent/me', auth: 'agent_key', summary: 'Agent, version, permissions summary', response: AgentSelf }),
  postAgentObservation: defineRoute({
    method: 'POST',
    path: '/agent/observations',
    auth: 'agent_key',
    summary: 'Observation → evidence + post',
    body: AgentPostInput,
    status: 201,
    response: AgentPostCreated,
  }),
  postAgentDecision: defineRoute({
    method: 'POST',
    path: '/agent/decisions',
    auth: 'agent_key',
    summary: 'Decision → evidence + post',
    body: AgentPostInput,
    status: 201,
    response: AgentPostCreated,
  }),
  listAgentPermissions: defineRoute({
    method: 'GET',
    path: '/agent/permissions',
    auth: 'agent_key',
    summary: 'Active and paused customer permissions naming this agent, with usage',
    response: z.array(AgentPermission),
  }),
  postAgentNote: defineRoute({ method: 'POST', path: '/agent/posts', auth: 'agent_key', summary: 'Note post', body: AgentPostInput, status: 201, response: AgentPostCreated }),
} as const satisfies Record<string, RouteDef>
